import {
    Box,
    HStack,
    Progress,
    Text,
    VStack,
} from "@chakra-ui/react";
import { Zap } from "lucide-react";

import Card from "../../Dashboard/components/Card";

const FunnelAnalytics = ({
    drivers = [],
    milestones = [],
}) => {
    const registered =
        drivers.filter(
            driver =>
                driver.driver
                    ?.driver_id
        ).length;

    const approved =
        drivers.filter(
            driver =>
                driver.driver
                    ?.status ===
                "Approved"
        ).length;

    const firstDelivery =
        new Set(
            milestones
                .filter(
                    milestone =>
                        milestone.milestone_type ===
                            "First Delivery Completed" &&
                        milestone.milestone_status ===
                            "Achieved"
                )
                .map(
                    milestone =>
                        milestone.driver_id
                )
        ).size;

    const tenDeliveries =
        new Set(
            milestones
                .filter(
                    milestone =>
                        milestone.milestone_type ===
                            "Ten Deliveries Completed" &&
                        milestone.milestone_status ===
                            "Achieved"
                )
                .map(
                    milestone =>
                        milestone.driver_id
                )
        ).size;

    const thirtyDeliveries =
        new Set(
            milestones
                .filter(
                    milestone =>
                        milestone.milestone_type ===
                            "Thirty Deliveries Completed" &&
                        milestone.milestone_status ===
                            "Achieved"
                )
                .map(
                    milestone =>
                        milestone.driver_id
                )
        ).size;

    const registeredToApproved =
        registered > 0
            ? Math.round(
                  (
                      approved /
                      registered
                  ) * 100
              )
            : 0;

    const approvedToFirst =
        approved > 0
            ? Math.round(
                  (
                      firstDelivery /
                      approved
                  ) * 100
              )
            : 0;

    const firstToTen =
        firstDelivery > 0
            ? Math.round(
                  (
                      tenDeliveries /
                      firstDelivery
                  ) * 100
              )
            : 0;

    const tenToThirty =
        tenDeliveries > 0
            ? Math.round(
                  (
                      thirtyDeliveries /
                      tenDeliveries
                  ) * 100
              )
            : 0;

    const stages = [
        {
            label:
                "Registered → Approved",
            value:
                registeredToApproved,
        },
        {
            label:
                "Approved → First Delivery",
            value:
                approvedToFirst,
        },
        {
            label:
                "First → 10 Deliveries",
            value:
                firstToTen,
        },
        {
            label:
                "10 → 30 Deliveries",
            value:
                tenToThirty,
        },
    ];

    const drops = [
        {
            stage:
                "Registered to Approved",
            drop:
                100 -
                registeredToApproved,
        },
        {
            stage:
                "Approved to First Delivery",
            drop:
                100 -
                approvedToFirst,
        },
        {
            stage:
                "First Delivery to 10 Deliveries",
            drop:
                100 -
                firstToTen,
        },
        {
            stage:
                "10 Deliveries to 30 Deliveries",
            drop:
                100 -
                tenToThirty,
        },
    ];

    const weakest =
        drops.sort(
            (
                a,
                b
            ) =>
                b.drop -
                a.drop
        )[0];

    return (
        <Card h="100%">
            <Text
                fontSize="2xl"
                fontWeight="700"
                color="#071D54"
                mb={8}
            >
                Conversion Funnel
            </Text>

            <VStack
                align="stretch"
                gap={6}
            >
                {stages.map(
                    stage => (
                        <Box
                            key={
                                stage.label
                            }
                        >
                            <HStack
                                justify="space-between"
                                mb={2}
                            >
                                <Text
                                    fontWeight="600"
                                    color="#071D54"
                                >
                                    {
                                        stage.label
                                    }
                                </Text>

                                <Text
                                    color="#2453FF"
                                    fontWeight="700"
                                >
                                    {
                                        stage.value
                                    }
                                    %
                                </Text>
                            </HStack>

                            <Progress.Root
                                value={
                                    stage.value
                                }
                                size="md"
                                borderRadius="full"
                            >
                                <Progress.Track bg="#E8EEFF">
                                    <Progress.Range bg="#2453FF" />
                                </Progress.Track>
                            </Progress.Root>
                        </Box>
                    )
                )}

                <Box
                    mt={4}
                    bg="#EEF4FF"
                    borderRadius="16px"
                    p={5}
                >
                    <HStack
                        align="flex-start"
                        gap={4}
                    >
                        <Box
                            color="#2453FF"
                            mt={1}
                        >
                            <Zap
                                size={22}
                                fill="#2453FF"
                            />
                        </Box>

                        <Box>
                            <Text
                                fontWeight="700"
                                color="#071D54"
                            >
                                Funnel Insight
                            </Text>

                            <Text
                                mt={2}
                                color="gray.600"
                                fontSize="sm"
                                lineHeight="tall"
                            >
                                The largest
                                conversion
                                drop occurs
                                between{" "}
                                <Text
                                    as="span"
                                    fontWeight="700"
                                    color="#071D54"
                                >
                                    {
                                        weakest.stage
                                    }
                                </Text>
                                , with a
                                drop-off of{" "}
                                <Text
                                    as="span"
                                    color="#2453FF"
                                    fontWeight="700"
                                >
                                    {
                                        weakest.drop
                                    }
                                    %
                                </Text>
                                . Improving
                                onboarding,
                                engagement
                                and support
                                during this
                                stage could
                                significantly
                                improve
                                overall
                                partner
                                performance.
                            </Text>
                        </Box>
                    </HStack>
                </Box>
            </VStack>
        </Card>
    );
};

export default FunnelAnalytics;